/**
 * NHAI GIS style engine (hybrid: DB asset_type + frontend rendering rules).
 * See: Guideline for symbol rendering and clustering.pdf
 */
import { Feature } from 'ol';
import type { FeatureLike } from 'ol/Feature';
import { LineString, Point } from 'ol/geom';
import Icon from 'ol/style/Icon';
import { Fill, Stroke, Style, Text as OlText, Circle as CircleStyle, RegularShape } from 'ol/style';
import {
  getLrsAssetTypeMeta,
  inferLrsGrade,
  LRS_ASSET_EVENT_TYPES,
  LRS_LINEAR_EVENT_TYPES,
  LRS_UTILITY_TYPES,
  type LrsGrade,
} from 'src/app/shared/constants/lrs-asset-types';

/** Cluster nearby point assets within this pixel distance (PDF §16). */
export const LRS_DEFAULT_CLUSTER_DISTANCE = 40;

/**
 * Pixel cluster distance by zoom: group when zoomed out, true position from ~z16.5.
 * `basePx` is the user/default distance used at small scales.
 */
export function clusterDistanceForZoom(
  zoom: number,
  basePx: number = LRS_DEFAULT_CLUSTER_DISTANCE
): number {
  const base = Number.isFinite(basePx) ? Math.max(0, basePx) : LRS_DEFAULT_CLUSTER_DISTANCE;
  if (!Number.isFinite(zoom) || zoom >= 16.5) return 0;
  if (zoom >= 15) return Math.round(base * 0.35);
  if (zoom >= 13) return Math.round(base * 0.7);
  return Math.round(base * 1.15);
}

/** Show map text labels only at street-level zoom (icons stay visible). */
export const LRS_LABEL_MAX_RESOLUTION = 4;

/** Midline icons on linear assets/utilities only when close enough to read. */
export const LRS_MIDLINE_ICON_MAX_RESOLUTION = 8;

/** Scale icons up when zoomed in (resolution below this). */
export const LRS_ICON_BOOST_MAX_RESOLUTION = 20;

export interface LrsSymbolRule {
  assetType: string;
  symbolCode: string;
  color: string;
  geometryKind: 'point' | 'linear';
  iconSrc?: string;
  lineWidth?: number;
  lineDash?: number[];
  clusterable: boolean;
}

export const LRS_ICON_BASE = 'assets/icons/lrs';

/** Resolve icon path from symbol_code or asset_type (PDF §8 — /assets/icons/). */
export function lrsIconPath(symbolOrType: string): string {
  return `${LRS_ICON_BASE}/${symbolOrType}.svg`;
}

/** Absolute URL for fetch / OL (works on any dev-server port). */
export function resolveLrsIconUrl(relativePath: string): string {
  if (typeof document !== 'undefined' && document.baseURI) {
    return new URL(relativePath, document.baseURI).href;
  }
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  return `${base}/${relativePath.replace(/^\//, '')}`;
}

/** Centralized symbol rules — semantic type in data, appearance here (PDF §5 hybrid). */
export const LRS_SYMBOL_RULES: Record<string, LrsSymbolRule> = {
  sign: {
    assetType: 'sign',
    symbolCode: 'sign',
    color: '#2563eb',
    geometryKind: 'point',
    iconSrc: lrsIconPath('sign'),
    clusterable: true,
  },
  lighting: {
    assetType: 'lighting',
    symbolCode: 'lighting',
    color: '#ca8a04',
    geometryKind: 'point',
    iconSrc: lrsIconPath('lighting'),
    clusterable: true,
  },
  culvert: {
    assetType: 'culvert',
    symbolCode: 'culvert',
    color: '#0891b2',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('culvert'),
    lineWidth: 5,
    lineDash: [8, 6],
    clusterable: false,
  },
  bridge: {
    assetType: 'bridge',
    symbolCode: 'bridge',
    color: '#7c3aed',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('bridge'),
    lineWidth: 7,
    clusterable: false,
  },
  crash_barrier: {
    assetType: 'crash_barrier',
    symbolCode: 'crash_barrier',
    color: '#dc2626',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('crash_barrier'),
    lineWidth: 4,
    lineDash: [10, 4, 2, 4],
    clusterable: false,
  },
  cctv: {
    assetType: 'cctv',
    symbolCode: 'cctv',
    color: '#7c3aed',
    geometryKind: 'point',
    iconSrc: lrsIconPath('cctv'),
    clusterable: true,
  },
  vms: {
    assetType: 'vms',
    symbolCode: 'vms',
    color: '#9333ea',
    geometryKind: 'point',
    iconSrc: lrsIconPath('vms'),
    clusterable: true,
  },
  atms: {
    assetType: 'atms',
    symbolCode: 'atms',
    color: '#6366f1',
    geometryKind: 'point',
    iconSrc: lrsIconPath('atms'),
    clusterable: true,
  },
  sos: {
    assetType: 'sos',
    symbolCode: 'sos',
    color: '#ef4444',
    geometryKind: 'point',
    iconSrc: lrsIconPath('sos'),
    clusterable: true,
  },
  geometry: {
    assetType: 'geometry',
    symbolCode: 'geometry',
    color: '#64748b',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('geometry'),
    lineWidth: 3,
    lineDash: [6, 6],
    clusterable: false,
  },
  pavement: {
    assetType: 'pavement',
    symbolCode: 'pavement',
    color: '#475569',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('pavement'),
    lineWidth: 6,
    clusterable: false,
  },
  cross_section: {
    assetType: 'cross_section',
    symbolCode: 'cross_section',
    color: '#0d9488',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('cross_section'),
    lineWidth: 4,
    lineDash: [4, 8],
    clusterable: false,
  },
  superelevation: {
    assetType: 'superelevation',
    symbolCode: 'superelevation',
    color: '#9333ea',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('superelevation'),
    lineWidth: 4,
    lineDash: [12, 4],
    clusterable: false,
  },
  crossover: {
    assetType: 'crossover',
    symbolCode: 'crossover',
    color: '#c026d3',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('crossover'),
    lineWidth: 6,
    lineDash: [14, 4, 4, 4],
    clusterable: false,
  },
  underpass: {
    assetType: 'underpass',
    symbolCode: 'underpass',
    color: '#0f766e',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('underpass'),
    lineWidth: 6,
    lineDash: [8, 6],
    clusterable: false,
  },
  overpass: {
    assetType: 'overpass',
    symbolCode: 'overpass',
    color: '#a21caf',
    geometryKind: 'linear',
    iconSrc: lrsIconPath('overpass'),
    lineWidth: 7,
    clusterable: false,
  },
};

for (const u of LRS_UTILITY_TYPES) {
  LRS_SYMBOL_RULES[u.id] = {
    assetType: u.id,
    symbolCode: u.symbolCode || u.id,
    color: u.color,
    geometryKind: u.geometryKind || 'linear',
    iconSrc: u.iconPath || lrsIconPath(u.id),
    lineWidth: 3,
    clusterable: u.geometryKind === 'point',
  };
}

for (const t of LRS_ASSET_EVENT_TYPES) {
  if (LRS_SYMBOL_RULES[t.id]) {
    LRS_SYMBOL_RULES[t.id].iconSrc = t.iconPath;
    LRS_SYMBOL_RULES[t.id].geometryKind = t.geometryKind;
    continue;
  }
  LRS_SYMBOL_RULES[t.id] = {
    assetType: t.id,
    symbolCode: t.symbolCode,
    color: t.color,
    geometryKind: t.geometryKind,
    iconSrc: t.iconPath,
    lineWidth: t.geometryKind === 'linear' ? 5 : undefined,
    clusterable: !!t.clusterable,
  };
}

/** All distinct icon URLs for preload (PDF §24 RULE 7 — symbol caching). */
export function allLrsIconUrls(): string[] {
  const urls = new Set<string>();
  for (const rule of Object.values(LRS_SYMBOL_RULES)) {
    if (rule.iconSrc) urls.add(rule.iconSrc);
  }
  for (const t of LRS_ASSET_EVENT_TYPES) {
    urls.add(lrsIconPath(t.id));
  }
  return [...urls];
}

/** Native pixel size of SVG viewBox (all icons use 24×24). */
export const LRS_ICON_PX = 24;

/** Preloaded images for OpenLayers Icon (must use `img` + `imgSize`, not raw src). */
const iconImgCache = new Map<string, HTMLImageElement>();
const iconStyleCache = new Map<string, Style>();
let preloadPromise: Promise<void> | null = null;
let iconsReady = false;

export function areLrsIconsReady(): boolean {
  return iconsReady;
}

/** Clear cached OL styles after icons load so map picks up images. */
export function clearLrsIconStyleCache(): void {
  iconStyleCache.clear();
}

function loadIconImage(relativePath: string): Promise<void> {
  if (iconImgCache.has(relativePath)) return Promise.resolve();
  const url = resolveLrsIconUrl(relativePath);
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      iconImgCache.set(relativePath, img);
      resolve();
    };
    img.onerror = () => {
      console.warn('[LRS] icon image load failed:', url);
      resolve();
    };
    img.src = url;
  });
}

/** Preload SVG icons as HTMLImageElement before map render (OL Icon requirement). */
export function preloadLrsIcons(): Promise<void> {
  if (preloadPromise) return preloadPromise;
  preloadPromise = Promise.all(allLrsIconUrls().map(loadIconImage)).then((): void => {
    iconsReady = iconImgCache.size > 0;
    clearLrsIconStyleCache();
  });
  return preloadPromise;
}

function olIconFromRule(
  rule: LrsSymbolRule,
  scale: number,
  anchor: [number, number] = [0.5, 0.5]
): Icon | undefined {
  if (!rule.iconSrc) return undefined;
  const img = iconImgCache.get(rule.iconSrc);
  if (!img) return undefined;
  const px = Math.max(16, Math.round(LRS_ICON_PX * scale));
  return new Icon({
    img,
    width: px,
    height: px,
    anchor,
    anchorXUnits: 'fraction',
    anchorYUnits: 'fraction',
  });
}

function fallbackPointImage(color: string, scale: number): CircleStyle {
  return new CircleStyle({
    radius: 10 * scale,
    fill: new Fill({ color }),
    stroke: new Stroke({ color: '#fff', width: 2 }),
  });
}

export function getSymbolRule(assetType: string | null | undefined): LrsSymbolRule | undefined {
  if (!assetType) return undefined;
  if (LRS_SYMBOL_RULES[assetType]) return LRS_SYMBOL_RULES[assetType];
  const meta = getLrsAssetTypeMeta(assetType);
  if (meta) return LRS_SYMBOL_RULES[meta.id];
  const linear = LRS_LINEAR_EVENT_TYPES.find((t) => t.id === assetType);
  if (linear) return LRS_SYMBOL_RULES[linear.id];
  return undefined;
}

export function symbolCodeForAssetType(assetType: string): string {
  return getSymbolRule(assetType)?.symbolCode || `asset_${assetType}`;
}

export function iconPathForAssetType(assetType: string): string {
  return getSymbolRule(assetType)?.iconSrc || lrsIconPath(assetType);
}

/** Attach symbol_code for style engine (PDF §3 approach 1 + hybrid). */
export function applySymbolProperties(feature: Feature, props?: Record<string, unknown>): void {
  const eventType = String(
    props?.['event_type'] ?? feature.get('event_type') ?? props?.['utility_type'] ?? feature.get('utility_type') ?? ''
  );
  const rule = getSymbolRule(eventType);
  if (rule) {
    feature.set('symbol_code', rule.symbolCode);
    feature.set('asset_type', eventType);
  }
  if (props) {
    Object.entries(props).forEach(([k, v]) => {
      if (v !== undefined && v !== null) feature.set(k, v);
    });
  }
  const grade = inferLrsGrade({
    grade: feature.get('grade') ?? props?.['grade'],
    classification: feature.get('classification') ?? props?.['classification'],
    depth_m: feature.get('depth_m') ?? props?.['depth_m'] as number | null,
    event_type: eventType,
    properties: props,
  });
  feature.set('grade', grade);
}

function featureGrade(feature: FeatureLike): LrsGrade {
  return inferLrsGrade({
    grade: feature.get('grade'),
    classification: feature.get('classification'),
    depth_m: feature.get('depth_m'),
    event_type: String(feature.get('event_type') || feature.get('utility_type') || feature.get('asset_type') || ''),
  });
}

function featureLifecycle(feature: FeatureLike): string {
  return String(feature.get('lifecycle_status') || 'operational').toLowerCase();
}

function featureCondition(feature: FeatureLike): string {
  return String(feature.get('condition') || '').toLowerCase();
}

function lifecycleTint(base: string, feature: FeatureLike): string {
  const life = featureLifecycle(feature);
  const cond = featureCondition(feature);
  if (life === 'decommissioned') return '#94a3b8';
  if (life === 'construction' || life === 'planned') return '#ea580c';
  if (life === 'maintenance') return '#d97706';
  if (cond === 'critical') return '#dc2626';
  return base;
}

function conditionHaloColor(feature: FeatureLike): string | null {
  const cond = featureCondition(feature);
  if (cond === 'critical') return '#dc2626';
  if (cond === 'poor') return '#ea580c';
  return null;
}

function withLifecycleStyles(base: Style | Style[], feature: FeatureLike, geom?: LineString | Point): Style[] {
  const list = Array.isArray(base) ? base : [base];
  const halo = conditionHaloColor(feature);
  if (halo && geom instanceof Point) {
    list.unshift(
      new Style({
        geometry: geom,
        image: new CircleStyle({
          radius: 16,
          stroke: new Stroke({ color: halo, width: 3 }),
          fill: new Fill({ color: 'rgba(0,0,0,0)' }),
        }),
        zIndex: 0,
      })
    );
  } else if (halo && geom instanceof LineString) {
    list.unshift(
      new Style({
        geometry: geom,
        stroke: new Stroke({ color: halo, width: 9, lineCap: 'round' }),
        zIndex: 0,
      })
    );
  }
  return list;
}

function muteHex(color: string, underground: boolean): string {
  if (!underground || !color.startsWith('#') || (color.length !== 7 && color.length !== 4)) return color;
  const hex = color.length === 4
    ? `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`
    : color;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},0.72)`;
}

function dashForGrade(grade: LrsGrade, ruleDash?: number[]): number[] | undefined {
  if (grade === 'underground') return ruleDash?.length ? ruleDash : [6, 4];
  if (grade === 'crossover') return ruleDash?.length ? ruleDash : [14, 4, 4, 4];
  return ruleDash;
}

/** GIS editor duct / AM overlay stroke from corridor grade. */
export function strokeOptsForGrade(
  grade: LrsGrade | string,
  colorHint?: string
): { color: string; width: number; lineDash?: number[] } {
  const g = inferLrsGrade({ grade });
  const color =
    g === 'crossover' ? '#c026d3' : g === 'underground' ? '#8e44ad' : colorHint || '#f39c12';
  return {
    color: muteHex(color, g === 'underground'),
    width: g === 'crossover' ? 4 : 3,
    lineDash: dashForGrade(g),
  };
}

/** 2.5D screen offset: buried utilities shift slightly "down" (south) in map metres. */
function geometryForGrade(feature: FeatureLike): LineString | Point | undefined {
  const g = feature.getGeometry();
  if (!(g instanceof LineString) && !(g instanceof Point)) return undefined;
  const grade = featureGrade(feature);
  const depth = Number(feature.get('depth_m'));
  if (grade === 'underground' && Number.isFinite(depth) && Math.abs(depth) > 0) {
    const clone = g.clone();
    clone.translate(0, -Math.abs(depth) * 1.5);
    return clone as LineString | Point;
  }
  return g as LineString | Point;
}

function crossoverMarkStyle(line: LineString, color: string): Style {
  return new Style({
    geometry: new Point(line.getCoordinateAt(0.5)),
    image: new RegularShape({
      points: 4,
      radius: 11,
      radius2: 0,
      angle: Math.PI / 4,
      stroke: new Stroke({ color, width: 2.4 }),
    }),
    zIndex: 4,
  });
}

function iconScaleBucket(resolution: number): string {
  if (resolution > 80) return 'xs';
  if (resolution > 40) return 'sm';
  if (resolution > 15) return 'md';
  if (resolution > 6) return 'lg';
  if (resolution > 2) return 'xl';
  return 'xxl';
}

function iconScaleForResolution(resolution: number): number {
  if (resolution > 80) return 0.55;
  if (resolution > 40) return 0.7;
  if (resolution > 15) return 0.85;
  if (resolution > 6) return 1.0;
  if (resolution > 2) return 1.12;
  return 1.22;
}

function midlineIconScale(resolution: number): number {
  return iconScaleForResolution(resolution) * 0.9;
}

function strokeWidthForResolution(base: number, resolution: number): number {
  const scale = resolution > 40 ? 0.45 : resolution > 15 ? 0.7 : 1;
  return Math.max(1.25, base * scale);
}

function mapLabelText(raw: string, resolution: number): string {
  const text = String(raw || '').trim();
  if (!text) return '';
  const max = resolution > 2.5 ? 16 : 28;
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function labelPlacement(feature: FeatureLike): { offsetX: number; offsetY: number; textAlign: 'left' | 'right' | 'center' } {
  const side = String(feature.get('side') || '').toUpperCase();
  const offsetM = Number(feature.get('offset_m'));
  if (side === 'L' || (Number.isFinite(offsetM) && offsetM < 0)) {
    return { offsetX: -14, offsetY: -6, textAlign: 'right' };
  }
  if (side === 'R' || (Number.isFinite(offsetM) && offsetM > 0)) {
    return { offsetX: 14, offsetY: -6, textAlign: 'left' };
  }
  const key = String(feature.get('event_type') || feature.get('utility_type') || feature.get('asset_type') || '');
  const slot = key ? (key.charCodeAt(0) + key.length) % 5 - 2 : 0;
  return { offsetX: slot * 8, offsetY: -16 - Math.abs(slot) * 3, textAlign: 'center' };
}

function pointLabelStyle(feature: FeatureLike, resolution: number, text: string): OlText | undefined {
  if (resolution > LRS_LABEL_MAX_RESOLUTION || !text) return undefined;
  const place = labelPlacement(feature);
  return new OlText({
    text: mapLabelText(text, resolution),
    offsetX: place.offsetX,
    offsetY: place.offsetY,
    textAlign: place.textAlign,
    font: '600 10px sans-serif',
    fill: new Fill({ color: '#1e293b' }),
    stroke: new Stroke({ color: '#fff', width: 3 }),
    overflow: false,
  });
}

function pointIconStyle(rule: LrsSymbolRule, resolution: number, feature: FeatureLike, label?: string): Style {
  const iconLoaded = rule.iconSrc ? iconImgCache.has(rule.iconSrc) : false;
  const showLabel = resolution <= LRS_LABEL_MAX_RESOLUTION && !!label;
  const cacheKey = `${rule.symbolCode}_${iconScaleBucket(resolution)}_${iconLoaded}`;
  if (iconLoaded && !showLabel) {
    const cached = iconStyleCache.get(cacheKey);
    if (cached) return cached;
  }

  const scale = iconScaleForResolution(resolution);
  const icon = olIconFromRule(rule, scale);
  const style = new Style({
    image: icon ?? fallbackPointImage(rule.color, scale),
    text: showLabel ? pointLabelStyle(feature, resolution, label || '') : undefined,
    zIndex: 6,
  });
  if (!showLabel && icon && iconStyleCache.size < 400) iconStyleCache.set(cacheKey, style);
  return style;
}

export function stylePointAsset(feature: FeatureLike, resolution: number): Style | Style[] {
  const et = String(feature.get('event_type') || feature.get('utility_type') || feature.get('asset_type') || '');
  const rule = getSymbolRule(et);
  const label = String(feature.get('label') || '');
  const geom = geometryForGrade(feature);
  let style: Style;
  if (rule) {
    style = pointIconStyle(rule, resolution, feature, label);
  } else {
    const meta = getLrsAssetTypeMeta(et);
    const color = lifecycleTint(meta?.color || '#ea580c', feature);
    style = new Style({
      image: fallbackPointImage(color, iconScaleForResolution(resolution)),
      text: pointLabelStyle(feature, resolution, label || et),
      zIndex: 6,
    });
  }
  return withLifecycleStyles(style, feature, geom instanceof Point ? geom : undefined);
}

export function styleLinearAsset(feature: FeatureLike, resolution: number): Style | Style[] {
  const et = String(feature.get('event_type') || feature.get('asset_type') || '');
  const rule = getSymbolRule(et);
  const grade = featureGrade(feature);
  const color = lifecycleTint(
    muteHex(rule?.color || getLrsAssetTypeMeta(et)?.color || '#ea580c', grade === 'underground'),
    feature
  );
  const width = strokeWidthForResolution(rule?.lineWidth || (grade === 'crossover' ? 6 : 5), resolution);
  const showLabel = resolution <= LRS_LABEL_MAX_RESOLUTION;
  const geom = geometryForGrade(feature);
  const styles: Style[] = [
    new Style({
      geometry: geom,
      stroke: new Stroke({
        color,
        width,
        lineDash: dashForGrade(grade, rule?.lineDash),
      }),
      text: showLabel
        ? new OlText({
            text: mapLabelText(String(feature.get('label') || et), resolution),
            placement: 'line',
            maxAngle: 0.4,
            fill: new Fill({ color: '#0f172a' }),
            stroke: new Stroke({ color: '#fff', width: 3 }),
            overflow: false,
          })
        : undefined,
    }),
  ];

  if (rule && geom instanceof LineString && resolution <= LRS_MIDLINE_ICON_MAX_RESOLUTION) {
    const mid = geom.getCoordinateAt(0.5);
    const midIcon = olIconFromRule(rule, midlineIconScale(resolution));
    if (midIcon) {
      styles.push(new Style({ geometry: new Point(mid), image: midIcon, zIndex: 5 }));
    } else {
      styles.push(
        new Style({
          geometry: new Point(mid),
          image: fallbackPointImage(rule.color, 0.7),
          zIndex: 5,
        })
      );
    }
  }
  if (geom instanceof LineString && (grade === 'crossover' || et === 'crossover' || et === 'overpass' || et === 'underpass')) {
    styles.push(crossoverMarkStyle(geom, color));
  }
  return withLifecycleStyles(styles, feature, geom instanceof LineString ? geom : undefined);
}

export function styleUtilityFeature(feature: FeatureLike, resolution: number): Style | Style[] {
  const g = geometryForGrade(feature) || feature.getGeometry();
  if (g instanceof LineString) {
    const t = String(feature.get('utility_type') || '');
    const rule = getSymbolRule(t);
    const grade = featureGrade(feature);
    const color = lifecycleTint(
      muteHex(
        rule?.color || LRS_UTILITY_TYPES.find((u) => u.id === t)?.color || '#0d9488',
        grade === 'underground'
      ),
      feature
    );
    const styles: Style[] = [
      new Style({
        geometry: g,
        stroke: new Stroke({
          color,
          width: strokeWidthForResolution(grade === 'crossover' ? 5 : rule?.lineWidth || 3, resolution),
          lineDash: dashForGrade(grade, rule?.lineDash),
        }),
      }),
    ];
    if (rule && resolution <= LRS_MIDLINE_ICON_MAX_RESOLUTION) {
      const mid = g.getCoordinateAt(0.5);
      const midIcon = olIconFromRule(rule, midlineIconScale(resolution));
      if (midIcon) {
        styles.push(new Style({ geometry: new Point(mid), image: midIcon, zIndex: 5 }));
      }
    }
    if (grade === 'crossover') {
      styles.push(crossoverMarkStyle(g, color));
    }
    return withLifecycleStyles(styles, feature, g);
  }
  return stylePointAsset(feature, resolution);
}

/** Cluster: dominant SVG icon + count badge (PDF §18). */
export function styleCluster(feature: FeatureLike, resolution: number): Style | Style[] {
  const members = feature.get('features') as Feature[] | undefined;
  if (!members?.length) return stylePointAsset(feature, resolution);
  if (members.length === 1) {
    const inner = members[0];
    const g = inner.getGeometry();
    if (g instanceof LineString) return styleLinearAsset(inner, resolution);
    return stylePointAsset(inner, resolution);
  }

  const size = members.length;
  const types = new Set(members.map((m) => String(m.get('event_type') || m.get('utility_type') || '')));
  const dominant =
    [...types].find((t) => getSymbolRule(t)?.geometryKind === 'point') || [...types][0] || 'asset';
  const rule = getSymbolRule(dominant);
  const color = rule?.color || '#1e40af';
  const iconScale = iconScaleForResolution(resolution) * 1.08;
  const clusterIcon = rule ? olIconFromRule(rule, iconScale) : undefined;

  if (clusterIcon) {
    return [
      new Style({ image: clusterIcon, zIndex: 2 }),
      new Style({
        zIndex: 3,
        image: new CircleStyle({
          radius: 9,
          fill: new Fill({ color: '#0f172a' }),
          stroke: new Stroke({ color: '#fff', width: 1.5 }),
        }),
        text: new OlText({
          text: String(size),
          font: 'bold 9px sans-serif',
          fill: new Fill({ color: '#fff' }),
          offsetX: 16,
          offsetY: -16,
        }),
      }),
    ];
  }

  const radius = Math.min(28, 12 + Math.log10(size) * 6);
  return new Style({
    image: new CircleStyle({
      radius,
      fill: new Fill({ color: `${color}cc` }),
      stroke: new Stroke({ color: '#fff', width: 2 }),
    }),
    text: new OlText({
      text: String(size),
      font: `bold ${size > 99 ? 11 : 12}px sans-serif`,
      fill: new Fill({ color: '#fff' }),
    }),
  });
}

export function styleForLrsFeature(feature: FeatureLike, resolution: number): Style | Style[] {
  const g = feature.getGeometry();
  if (g instanceof LineString) return styleLinearAsset(feature, resolution);
  if (g instanceof Point) return stylePointAsset(feature, resolution);
  return stylePointAsset(feature, resolution);
}
